import { quarriesApi, userApi } from '@api/queries';
import { authApi } from '@api/queries/auth';
import { contractApi } from '@api/queries/contract';
import { factoryApi } from '@api/queries/factory';
import { workshopApi } from '@api/queries/workshop';
import { filterRequirementsByType } from '@common/lib/getRequirementsByType';
import { type RequirementsItemProps } from '@common/types/components';
import { type UserFactory } from '@common/types/factoryType';
import { type UserQuarry } from '@common/types/quarryType';
import { type ClaimTaskRewardResponseType } from '@common/types/query/tasks';
import {
  type ActivateVipStatusResponseType,
  type UserReferralEarningsType,
  type VipInfoResponseType,
  type WithdrawHoldedMoneyResponseType,
} from '@common/types/query/user';
import {
  type Balance,
  type FormatedTwoGroups,
  type MaterialDetailsType,
  type UnionGameItemType,
  type User,
  type UserExtendedInfoType,
} from '@common/types/userType';
import { type UserWorkshop } from '@common/types/workshopType';
import { createSlice, isAnyOf, type PayloadAction } from '@reduxjs/toolkit';
import { setUserId } from 'firebase/analytics';

import { boostApi } from '@/api/queries/boost';
import { diceGameApi } from '@/api/queries/game';
import { leagueApi } from '@/api/queries/league';
import { notificationsApi } from '@/api/queries/notifications';
import { paymentApi } from '@/api/queries/payment';
import { shopApi } from '@/api/queries/shop';
import { storageApi } from '@/api/queries/storage';
import { plus } from '@/common/lib/decimal';
import { calcHasDailyBonusVisit } from '@/common/lib/generateDailyBonusState';
import { type Airdrop } from '@/common/types/airdrop';
import { type CommoditiesType } from '@/common/types/common';
import { type SpecContractType } from '@/common/types/contract';
import {
  type BoxRewardsType,
  type CollectCardGameRewardsResponseType,
  type SaveAfterBombEventResponseType,
} from '@/common/types/dice-game';
import { type LeagueGetRewardResponseType } from '@/common/types/league';
import type { NotificationDto } from '@/common/types/notification';
import { type BoostInstantCompleteResponse } from '@/common/types/query/boost';
import { type ActivatePromoCodeResponseType } from '@/common/types/shop/marketBoxes';
import { analytics } from '@/firebase';

interface UserState {
  user: User | null;
  extendedInfo: UserExtendedInfoType | null;
  airdrop: Airdrop | null;
  referralEarnings: UserReferralEarningsType | null;
  vip: null | VipInfoResponseType;
  isFirstVisit?: boolean;
  firstVipRegistration?: boolean;
  showOnboarding?: boolean;
  showWelcomeArena?: boolean;
  serverErrorStatus?: string | null;
  maintenanceEndTime?: string | null;
  maintenanceWakeUpAt?: string | null;
  hasDailyBonusVisit: boolean;
  tutorial: Record<SpecContractType, boolean>;
  token: string | null;
  activeVipTimeInSeconds: number;
  notifications: NotificationDto[];
  unreadNotifications: number;
  nextTimeToRefillEnergyForTgem?: string;
}

const initialState: UserState = {
  user: null,
  extendedInfo: null,
  airdrop: null,
  referralEarnings: null,
  firstVipRegistration: true,
  vip: null,
  hasDailyBonusVisit: calcHasDailyBonusVisit(),
  tutorial: { Custom: false, Golden: false, Grand: false, Blitz: false },
  token: localStorage.getItem('auth_token'),
  activeVipTimeInSeconds: 0,
  notifications: [],
  unreadNotifications: 0,
};

const userSlice = createSlice({
  name: 'authUser',
  initialState,
  reducers: {
    setServerError: (state, { payload }) => {
      state.serverErrorStatus = payload;
    },
    clearServerError: (state) => {
      state.serverErrorStatus = null;
    },
    setMaintenanceMode: (state, { payload }: PayloadAction<string | null>) => {
      state.maintenanceEndTime = payload;
      state.maintenanceWakeUpAt = payload;
    },
    clearMaintenanceMode: (state) => {
      state.maintenanceEndTime = null;
      state.maintenanceWakeUpAt = null;
    },
    setChangeGroup: (state, { payload }: PayloadAction<FormatedTwoGroups>) => {
      if (state.user) state.user.changeGroup = payload;
    },
    removeChangeGroup: (state) => {
      if (state.user) state.user.changeGroup = null;
    },
    removeMessageNotFoundGroup: (state) => {
      if (state.user) state.user.groupNotFound = null;
    },
    updateStore: (state, action) => {
      if (state.user) {
        state.user.storage = action.payload;
      }
    },
    updateBalance: (state, action) => {
      if (state.user) {
        state.user.balance = action.payload;
      }
    },
    updateEnergy: (state, { payload }: PayloadAction<number>) => {
      if (state.user && state.extendedInfo) {
        state.extendedInfo.energy =
          payload > state.user.settings.energyLimit ? 1000 : payload;
      }
    },
    updateLevel: (state, action: PayloadAction<number>) => {
      if (state.user) {
        state.user.level = action.payload;
      }
    },
    // бэкенд при смене имени стирает фамилию из Telegram
    updateFirstName: (state, { payload }: PayloadAction<string>) => {
      if (state.user) {
        state.user.firstName = payload;
        state.user.lastName = '';
      }
    },
    changeEnergy: (state, { payload }: PayloadAction<number>) => {
      if (state.user && state.extendedInfo) {
        const result = state.extendedInfo.energy + payload;
        const energy =
          result > state.user.settings.energyLimit
            ? 1000
            : result < 0
              ? 0
              : result;

        state.extendedInfo.energy = energy;
      }
    },
    recoveryEnergy: (state) => {
      if (
        state.user &&
        state.extendedInfo &&
        state.extendedInfo.energy < state.user.settings.energyLimit
      ) {
        state.extendedInfo.energy += 1;
      }
    },
    addToBalance: (state, { payload }: PayloadAction<Partial<Balance>>) => {
      if (state.user) {
        if (payload.cash) {
          state.user.balance.cash =
            (state.user.balance.cash || 0) + payload.cash;
        }

        if (payload.point) {
          state.user.balance.point =
            (state.user.balance.point || 0) + payload.point;
        }

        if (payload.str) {
          state.user.balance.str = (state.user.balance.str || 0) + payload.str;
        }

        if (payload.ton) {
          state.user.balance.ton = (state.user.balance.ton || 0) + payload.ton;
        }

        if (payload.tgem) {
          state.user.balance.tgem =
            (state.user.balance.tgem || 0) + payload.tgem;
        }

        if (payload.casinoTickets) {
          state.user.balance.casinoTickets =
            (state.user.balance.casinoTickets || 0) + payload.casinoTickets;
        }

        if (payload.dice) {
          state.user.balance.dice =
            (state.user.balance.dice || 0) + payload.dice;
        }

        if (payload.usdt) {
          const balanceValue = state.user.balance.usdt || 0;
          const currentBalanceInCents = Math.round(balanceValue * 1000);
          const valueInCents = Math.round(payload.usdt * 1000);

          const newBalanceInCents = currentBalanceInCents + valueInCents;

          state.user.balance.usdt = Math.round(newBalanceInCents) / 1000;
        }

        if (payload.bingoBalls) {
          state.user.balance.bingoBalls =
            (state.user.balance.bingoBalls || 0) + payload.bingoBalls;
        }
      }
    },
    setBingoBalls: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.balance.bingoBalls = payload;
      }
    },
    changeStorage: (
      state,
      { payload }: PayloadAction<Record<string, number>>,
    ) => {
      if (state.user?.storage) {
        const storage = state.user?.storage;
        for (const item in payload) {
          if (item in storage) {
            const plant = storage[item] as MaterialDetailsType;
            plant.value -= payload[item];
          }
        }
      }
    },
    addToStorage: (
      state,
      {
        payload,
      }: PayloadAction<
        | { item: string; value: number; costPerUnit?: number }
        | { item: string; value: number; costPerUnit?: number }[]
      >,
    ) => {
      if (state.user?.storage) {
        const storage = state.user?.storage;

        // Перевірка, чи payload є масивом
        if (Array.isArray(payload)) {
          payload.forEach((item) => {
            if (item.item in storage) {
              const plant = storage[item.item] as MaterialDetailsType;
              plant.value += item.value;
            } else {
              storage[item.item] = {
                value: item.value,
                costPerUnit: item.costPerUnit || 1,
              };
            }
          });
        } else {
          // Обробка одиничного об'єкта
          if (payload.item in storage) {
            const plant = storage[payload.item] as MaterialDetailsType;
            plant.value += payload.value;
          } else {
            storage[payload.item] = {
              value: payload.value,
              costPerUnit: payload.costPerUnit || 1,
            };
          }
        }
      }
    },
    updateStorageAndBalance: (
      state,
      { payload }: PayloadAction<RequirementsItemProps[] | undefined>,
    ) => {
      if (!payload) {
        return;
      }

      const balance = filterRequirementsByType({
        list: payload,
        type: 'cash',
      })[0];
      const materials = filterRequirementsByType({
        list: payload,
        type: 'material',
      });

      if (balance && state.user?.balance !== undefined) {
        state.user.balance.cash -= balance.value;
      }

      if (state.user?.storage) {
        const storage = state.user.storage;

        materials.forEach((material) => {
          if (material.resourceName) {
            const name = material.resourceName;

            if (name in storage && storage[name]?.value) {
              storage[name].value -= material.value;
            }
          }
        });
      }
    },
    subtractBalance: (
      state,
      { payload }: PayloadAction<{ type: keyof Balance; value: number }>,
    ) => {
      const balanceValue = state.user?.balance[payload.type];
      const { type, value } = payload;

      if (state.user && balanceValue) {
        let newValue = balanceValue - value;

        if (type === 'usdt') {
          const currentBalanceInCents = Math.round(balanceValue * 1000);
          const valueInCents = Math.round(value * 1000);

          const newBalanceInCents = currentBalanceInCents - valueInCents;

          newValue = Math.round(newBalanceInCents) / 1000;
        }

        state.user.balance = {
          ...state.user.balance,
          [payload.type]: newValue,
        };
      }
    },
    endIntro: (state) => {
      state.isFirstVisit = false;
    },
    endFirstVipRegistration: (state) => {
      state.firstVipRegistration = false;
    },
    changeOnboardingStatus: (state, { payload }: PayloadAction<boolean>) => {
      state.showOnboarding = payload;
    },
    endWelcomeArena: (state) => {
      state.showWelcomeArena = false;
    },
    updateCoinBalance: (state, { payload }: PayloadAction<number>) => {
      if (state.user) {
        state.user.balance.tgem = payload;
      }
    },
    changeVipStatus: (state, { payload }: PayloadAction<boolean>) => {
      if (state.user) {
        state.user.VIP = payload;

        if (state.vip) {
          state.vip.VIP = payload;
        }
      }
    },
    acitvateVipStatus: (
      state,
      { payload }: PayloadAction<ActivateVipStatusResponseType>,
    ) => {
      if (state.vip) {
        Object.assign(state.vip, payload);
      }
    },
    claimDailyTgemBonus: (
      state,
      { payload }: PayloadAction<'free' | 'advertising'>,
    ) => {
      if (state.extendedInfo) {
        state.extendedInfo.dailyTgemsBonus.push(payload);
      }
    },
    clearDailyTgemBonus: (state) => {
      if (state.extendedInfo) {
        state.extendedInfo.dailyTgemsBonus = [];
      }
    },
    markFreeDailyDiceClaimed: (state) => {
      const freePackage = state.extendedInfo?.dicePackages.find(
        (pkg) => pkg.free !== undefined,
      );

      if (freePackage) {
        freePackage.free = false;
      }
    },
    visitDailyBonus: (state) => {
      state.hasDailyBonusVisit = true;
    },
    claimDiceGameBoxRewards: (
      state,
      { payload }: PayloadAction<BoxRewardsType>,
    ) => {
      if (!state.user) return;

      for (const reward in payload) {
        const value = payload[reward as keyof BoxRewardsType] as number;

        if (reward === 'cash') {
          state.user.balance.cash += value;
          continue;
        }

        if (reward === 'tgem') {
          state.user.balance.tgem += value;
          continue;
        }

        if (state.user.storage[reward]) {
          state.user.storage[reward].value += value;
        } else {
          state.user.storage[reward] = {
            value,
            costPerUnit: 1,
          };
        }
      }
    },
    changeTutorial: (
      state,
      { payload }: PayloadAction<{ userId: string; kind: SpecContractType }>,
    ) => {
      const { userId, kind } = payload;
      const keys: Record<SpecContractType, string> = {
        Golden: 'tutorialGolden',
        Grand: 'tutorialGrand',
        Custom: 'tutorialCustom',
        Blitz: 'tutorialBiltz',
      };
      const key = keys[kind];

      const currentUserId = localStorage.getItem(key);

      if (!currentUserId || currentUserId !== userId) {
        localStorage.setItem(key, userId);
      }

      state.tutorial[kind] = true;
    },
    loggedOut: (state) => {
      state.token = null;
    },
    setToken: (state, { payload }: PayloadAction<string>) => {
      state.token = payload;
    },
    setActiveVipTimeInSeconds: (state, { payload }: PayloadAction<number>) => {
      state.activeVipTimeInSeconds = payload;
    },
    completeStepToNewLevel: (state, { payload }: PayloadAction<string>) => {
      if (!state.extendedInfo?.stepPlantsNextLevel) return;

      const currentStep = state.extendedInfo?.stepPlantsNextLevel.find(
        ({ id }) => id === payload,
      );

      if (currentStep) {
        currentStep.complete = true;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Смена скина в настройках: строка рейтинга читает decorationId из
      // стора, поэтому её нужно обновить, не дожидаясь перезапроса /me.
      .addMatcher(
        isAnyOf(userApi.endpoints.selectUserDecoration.matchFulfilled),
        (state, { payload }) => {
          if (state.user) {
            state.user.decorationId = payload.activeId;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          storageApi.endpoints.sellStorageItem.matchFulfilled,
          storageApi.endpoints.sellAllStorageItems.matchFulfilled,
        ),
        (state, { payload }) => {
          if (state.user?.storage && state.user?.balance && payload) {
            state.user.storage = payload.storage;
            state.user.balance = payload.balance;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          userApi.endpoints.authTelegram.matchFulfilled,
          userApi.endpoints.getMyInfo.matchFulfilled,
          authApi.endpoints.tonLogin.matchFulfilled,
          authApi.endpoints.guestLogin.matchFulfilled,
          authApi.endpoints.appleLogin.matchFulfilled,
        ),
        (state, action) => {
          // state.loading = false;
          state.user = action.payload;
          state.token = action.payload.token;

          if (analytics) setUserId(analytics, action.payload.id);
          const isFirstVisit = !('wood' in action.payload.plants);
          state.isFirstVisit = isFirstVisit;
          const currentUserId = action.payload.id;

          const storedCustomUserId = localStorage.getItem('tutorialCustom');
          state.tutorial.Custom = storedCustomUserId === currentUserId;

          const storedGoldenUserId = localStorage.getItem('tutorialGolden');
          state.tutorial.Golden = storedGoldenUserId === currentUserId;

          const storedGrandUserId = localStorage.getItem('tutorialGolden');
          state.tutorial.Grand = storedGrandUserId === currentUserId;

          const storedBlitzUserId = localStorage.getItem('tutorialBiltz');
          state.tutorial.Blitz = storedBlitzUserId === currentUserId;

          // Перевіряємо, чи дія відповідає userApi.endpoints.authUser.matchFulfilled
          // if (userApi.endpoints.authTelegram.matchFulfilled(action)) {

          // }
        },
      )
      .addMatcher(
        isAnyOf(userApi.endpoints.extendedInfo.matchFulfilled),
        (state, action) => {
          // state.loading = false;
          state.extendedInfo = action.payload;
          state.nextTimeToRefillEnergyForTgem = new Date(
            Date.now() +
              action.payload.coolDownBeforeEnergyRefillForTgems * 1000,
          ).toISOString();
          state.vip = action.payload.vipInfo;

          if (state.user) {
            state.user.VIP = !!action.payload.vipInfo?.VIP;
          }

          if (state.user) {
            state.user.level = action.payload.level;
          }
        },
      )
      .addMatcher(
        leagueApi.endpoints.getNewLeagueStarted.matchFulfilled,
        (state, { payload }) => {
          if (payload === null) {
            localStorage.removeItem('LEAGUE_ID_CACHE_KEY');
            state.showWelcomeArena = false;
          } else {
            const cachedLeagueId = localStorage.getItem('LEAGUE_ID_CACHE_KEY');

            if (cachedLeagueId !== payload.id) {
              state.showWelcomeArena = true;
              localStorage.setItem('LEAGUE_ID_CACHE_KEY', payload.id);
            } else {
              state.showWelcomeArena = false;
            }
          }
        },
      )
      .addMatcher(
        isAnyOf(quarriesApi.endpoints.claimAllResources.matchFulfilled),
        (state, { payload }) => {
          if (state.user) {
            state.user.storage = payload.storage;
          }
        },
      )
      .addMatcher(
        userApi.endpoints.claimTaskById.matchFulfilled,
        (state, { payload }: PayloadAction<ClaimTaskRewardResponseType>) => {
          if (state.user) {
            state.user.balance = { ...state.user.balance, ...payload.balance };
            state.user.storage = payload.storage;
          }
        },
      )
      .addMatcher(
        userApi.endpoints.getReferralEarnings.matchFulfilled,
        (state, { payload }: PayloadAction<UserReferralEarningsType>) => {
          state.referralEarnings = payload;
        },
      )
      .addMatcher(
        userApi.endpoints.withdrawHoldedMoney.matchFulfilled,
        (
          state,
          { payload }: PayloadAction<WithdrawHoldedMoneyResponseType>,
        ) => {
          if (state.user) {
            state.user.balance = payload.balance;
            state.user.holdedBalance = payload.onHold;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          factoryApi.endpoints.claimAllMaterials.matchFulfilled,
          factoryApi.endpoints.claimMaterialById.matchFulfilled,
          factoryApi.endpoints.clearFactoryProduction.matchFulfilled,
          quarriesApi.endpoints.claimResourceById.matchFulfilled,
          quarriesApi.endpoints.claimAllResources.matchFulfilled,
          workshopApi.endpoints.claimAllGoods.matchFulfilled,
          workshopApi.endpoints.claimGoodsById.matchFulfilled,
          workshopApi.endpoints.clearWorkshopProduction.matchFulfilled,
          factoryApi.endpoints.putAllFactory.matchFulfilled,
        ),
        (state, { payload }) => {
          if (state.user) {
            state.user.storage = payload.storage;
          }
        },
      )
      .addMatcher(
        isAnyOf(
          quarriesApi.endpoints.upgradeQuarry.matchFulfilled,
          factoryApi.endpoints.upgradeFactory.matchFulfilled,
          workshopApi.endpoints.upgradeWorkshop.matchFulfilled,
        ),
        (
          state,
          { payload }: PayloadAction<UserQuarry | UserFactory | UserWorkshop>,
        ) => {
          const plantData = {
            value: payload.level,
            costPerUnit: 'costPerUnit' in payload ? payload.costPerUnit : 0,
            type: '',
          };

          if (state.user) {
            if (state.user?.plants) {
              state.user.plants[payload.name as UnionGameItemType] = plantData;
            } else {
              state.user.plants = {
                [payload.name]: plantData,
              };
            }
          }
        },
      )
      .addMatcher(
        isAnyOf(
          quarriesApi.endpoints.buildQuarry.matchFulfilled,
          factoryApi.endpoints.buildFactory.matchFulfilled,
          workshopApi.endpoints.buildWorkshop.matchFulfilled,
        ),
        (
          state,
          {
            payload,
          }: PayloadAction<UserQuarry[] | UserFactory[] | UserWorkshop[]>,
        ) => {
          if (state.user) {
            payload.forEach((plant) => {
              if (state.user?.plants) {
                const plantsData = state.user?.plants;

                if (plantsData[plant.name as UnionGameItemType]) {
                  const plantData = plantsData[
                    plant.name as UnionGameItemType
                  ] as {
                    value: number;
                    costPerUnit: number;
                    type: string;
                  };
                  plantData.value = plant.level;
                } else {
                  plantsData[plant.name as UnionGameItemType] = {
                    value: plant.level,
                    costPerUnit: 'costPerUnit' in plant ? plant.costPerUnit : 1,
                    type: '',
                  };
                }
              }
            });
          }
        },
      )
      .addMatcher(
        contractApi.endpoints.applyChest.matchFulfilled,
        (state, { payload }) => {
          console.log(payload);

          if (state.user) {
            state.user.balance = payload.balance;
            state.user.storage = payload.storage;
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.tapQuarry.matchRejected,
        (state, action) => {
          const args = action.meta.arg.originalArgs;

          if (state.user && state.extendedInfo) {
            state.extendedInfo.energy += args.boosts || 1;
          }
        },
      )
      .addMatcher(
        boostApi.endpoints.boostInstantComplete.matchFulfilled,
        (state, { payload }: PayloadAction<BoostInstantCompleteResponse>) => {
          if (state.user) {
            state.user.balance = payload.balance;
          }
        },
      )
      .addMatcher(
        quarriesApi.endpoints.tapQuarry.matchFulfilled,
        (state, { payload }) => {
          if (state.extendedInfo) {
            state.extendedInfo.lastEnergyUpdated = payload.lastEnergyUpdated;
          }
        },
      )
      .addMatcher(
        leagueApi.endpoints.getLeagueReward.matchFulfilled,
        (state, { payload }: PayloadAction<LeagueGetRewardResponseType>) => {
          if (state.user) {
            state.user.balance.cash += payload.wallet.cash || 0;
            state.user.balance.tgem += payload.wallet.tgem || 0;
            const usdtBalance = state.user.balance.usdt;
            state.user.balance.usdt =
              (usdtBalance || 0) + (payload.wallet.usdt || 0);

            for (const resource in payload.materials) {
              if (
                resource in state.user.storage &&
                state.user.storage[resource]
              ) {
                state.user.storage[resource].value +=
                  payload.materials[resource as CommoditiesType] || 0;
              } else {
                state.user.storage[resource] = {
                  value: payload.materials[resource as CommoditiesType] || 0,
                  costPerUnit: 1,
                };
              }
            }
          }
        },
      )
      .addMatcher(
        userApi.endpoints.refillEnergy.matchFulfilled,
        (state, { payload }) => {
          if (state.extendedInfo) {
            state.extendedInfo.coolDownBeforeEnergyRefillForTgems =
              payload.coolDown;
            state.nextTimeToRefillEnergyForTgem = new Date(
              Date.now() + payload.coolDown * 1000,
            ).toISOString();
          }
        },
      )
      .addMatcher(
        userApi.endpoints.getVipInfo.matchFulfilled,
        (state, { payload }) => {
          state.vip = payload;

          // if(state.user && payload.VIP !== undefined) {
          //   state.user.VIP = payload.VIP;
          // }
        },
      )
      .addMatcher(
        isAnyOf(
          diceGameApi.endpoints.collectCardGameRewards.matchFulfilled,
          diceGameApi.endpoints.saveAfterBombEvent.matchFulfilled,
        ),
        (
          state,
          {
            payload,
          }: PayloadAction<
            CollectCardGameRewardsResponseType | SaveAfterBombEventResponseType
          >,
        ) => {
          if (!state.user) return;

          const reward = payload.totalReward;

          if (reward.cash) {
            state.user.balance.cash += reward.cash;
          }

          for (const gameItem in reward) {
            if (gameItem === 'cash') continue;

            const value = reward[gameItem as keyof typeof reward] as number;

            if (state.user.storage[gameItem]) {
              state.user.storage[gameItem].value += value;
            } else {
              state.user.storage[gameItem] = {
                costPerUnit: 1,
                value,
              };
            }
          }
        },
      )
      .addMatcher(
        userApi.endpoints.getInitialRewards.matchFulfilled,
        (state, action) => {
          if (!state.user) return;
          const { energy, wallet, storage } = action.payload;

          // extendedInfo запрашивается параллельно со стартовыми наградами и
          // может прийти позже — баланс всё равно пишем, иначе онбординг
          // не видит денег на первую стройку до следующего обновления юзера
          if (state.extendedInfo) {
            state.extendedInfo.energy = energy;
          }
          state.user.balance.cash = wallet.cash;
          state.user.balance.tgem = wallet.tgem;
          state.user.storage = storage;
        },
      )
      .addMatcher(
        userApi.endpoints.getOnboardingRewards.matchFulfilled,
        (state, action) => {
          if (!state.user) return;
          const { tgem, ton = 0, usdt = 0, cash = 0 } = action.payload;

          state.user.balance.tgem = tgem;
          state.user.balance.ton = ton;
          state.user.balance.usdt = usdt;
          state.user.balance.cash = cash;
        },
      )
      .addMatcher(
        paymentApi.endpoints.withdrawRequest.matchFulfilled,
        (state) => {
          if (state.user) {
            state.user.balance.usdt = 0;
          }
        },
      )
      .addMatcher(
        userApi.endpoints.airdrop.matchFulfilled,
        (state, { payload }: PayloadAction<Airdrop>) => {
          if (state.user) {
            state.airdrop = payload;
          }
        },
      )
      .addMatcher(
        shopApi.endpoints.activatePromoCode.matchFulfilled,
        (state, { payload }: PayloadAction<ActivatePromoCodeResponseType>) => {
          if (!state.user) return;

          if (payload.rewards.wallet) {
            for (const field in payload.rewards.wallet) {
              const value = payload.rewards.wallet[field as keyof Balance] || 0;
              const item = field as keyof typeof state.user.balance;
              state.user.balance = {
                ...state.user.balance,
                [item]: plus(state.user.balance[item] || 0, value),
              };
            }
          }

          if (payload.rewards.materials) {
            for (const field in payload.rewards.materials) {
              const value =
                payload.rewards.materials[
                  field as keyof typeof payload.rewards.materials
                ] || 0;
              const item = field as keyof typeof state.user.storage;
              state.user.storage = {
                ...state.user.storage,
                [item]: state.user.storage[item]
                  ? {
                      ...state.user.storage[item],
                      value: plus(state.user.storage[item].value, value),
                    }
                  : {
                      costPerUnit: 1,
                      value,
                    },
              };
            }
          }

          if (payload.rewards.vip) {
            state.user.VIP = true;
            const validUntil = state.vip?.validUntil;
            const validUntilMs = validUntil
              ? new Date(validUntil).getTime()
              : 0;
            const hasValidTimePassed = validUntil
              ? validUntilMs <= Date.now()
              : true;

            const timeEndsOfVip = hasValidTimePassed
              ? Date.now()
              : validUntilMs;
            const newTimeEndsOfVip =
              timeEndsOfVip + payload.rewards.vip * 60 * 60 * 1000;

            if (state.vip) {
              state.vip.VIP = true;
              state.vip.validUntil = new Date(newTimeEndsOfVip).toISOString();
            }
          }
        },
      )
      .addMatcher(
        userApi.endpoints.claimThankYouReward.matchFulfilled,
        (state, action) => {
          if (state.user) {
            state.user.balance.tgem += action.meta.arg.originalArgs.tgem ?? 0;
            state.user.balance.dice += action.meta.arg.originalArgs.dice ?? 0;
          }
        },
      );
    builder.addMatcher(
      notificationsApi.endpoints.getNotificationsAll.matchFulfilled,
      (state, { payload, meta }) => {
        const skip = meta.arg.originalArgs.skip ?? 0;
        const newNotifications = payload.notifications || [];

        if (skip === 0) {
          state.notifications = newNotifications;
        } else {
          const existingIds = new Set(state.notifications.map((n) => n.id));
          const uniqueNew = newNotifications.filter(
            (n) => !existingIds.has(n.id),
          );
          state.notifications = [...state.notifications, ...uniqueNew];
        }
        state.unreadNotifications = payload.unreadCount;
      },
    );
    builder.addMatcher(authApi.endpoints.linkApple.matchFulfilled, (state) => {
      if (state.user) state.user.appleLinked = true;
    });
    builder.addMatcher(
      notificationsApi.endpoints.readAllNotifications.matchFulfilled,
      (state) => {
        state.notifications = state.notifications.map((notification) => ({
          ...notification,
          readAt: new Date().toISOString(),
        }));
        state.unreadNotifications = 0;
      },
    );
    builder.addMatcher(
      notificationsApi.endpoints.readNotificationById.matchFulfilled,
      (state, { meta }) => {
        const { id } = meta.arg.originalArgs;
        const notificationIndex = state.notifications.findIndex(
          (notification) => notification.id === id,
        );
        state.unreadNotifications--;

        if (notificationIndex !== -1)
          state.notifications[notificationIndex] = {
            ...state.notifications[notificationIndex],
            readAt: new Date().toISOString(),
          };
      },
    );
  },
});

export const {
  addToBalance,
  updateFirstName,
  updateStore,
  updateBalance,
  updateEnergy,
  recoveryEnergy,
  changeEnergy,
  setChangeGroup,
  removeChangeGroup,
  removeMessageNotFoundGroup,
  updateStorageAndBalance,
  changeStorage,
  addToStorage,
  endIntro,
  endFirstVipRegistration,
  changeOnboardingStatus,
  endWelcomeArena,
  updateCoinBalance,
  setServerError,
  clearServerError,
  changeVipStatus,
  acitvateVipStatus,
  claimDailyTgemBonus,
  clearDailyTgemBonus,
  markFreeDailyDiceClaimed,
  visitDailyBonus,
  claimDiceGameBoxRewards,
  changeTutorial,
  loggedOut,
  setToken,
  updateLevel,
  subtractBalance,
  setActiveVipTimeInSeconds,
  completeStepToNewLevel,
  setMaintenanceMode,
  clearMaintenanceMode,
  setBingoBalls,
} = userSlice.actions;
export default userSlice.reducer;
